import { FarmWithStakedValue } from '@pulsex/farms'
import { useState } from 'react'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { Card, ExpandableSectionButton, Flex, Text, Skeleton } from '@pulsex/uikit'
import { FarmWidget } from '@pulsex/widgets-internal'
import { getBlockExplorerLink } from 'utils'
import { useTranslation } from '@pulsex/localization'
import getLiquidityUrlPathParts from 'utils/getLiquidityUrlPathParts'
import { useActiveChainId } from 'hooks/useActiveChainId'
import CardHeading from './CardHeading'
import CardActionsContainer from './CardActionsContainer'
import ApyButton from './ApyButton'

const { DetailsSection } = FarmWidget.FarmCard

const StyledCard = styled(Card)`
  align-self: baseline;
`

const FarmCardInnerContainer = styled(Flex)`
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
`

const ExpandingWrapper = styled.div`
  padding: 24px;
  border-top: 2px solid ${({ theme }) => theme.colors.cardBorder};
  overflow: hidden;
`

interface FarmCardProps {
  farm: FarmWithStakedValue
  displayApr: string
  removed: boolean
  incPrice?: BigNumber
  account?: string
  originalLiquidity?: BigNumber
}

const FarmCard: React.FC<React.PropsWithChildren<FarmCardProps>> = ({
  farm,
  displayApr,
  removed,
  incPrice,
  account,
}) => {
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()

  const [showExpandableSection, setShowExpandableSection] = useState(false)

  const totalValueFormatted =
    farm.liquidity && farm.liquidity.gt(0)
      ? `$${farm.liquidity.toNumber().toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : ''

  const lpLabel = farm.lpSymbol && farm.lpSymbol.replace('Incentive', '')
  const earnLabel = t('INC')

  const liquidityUrlPathParts = getLiquidityUrlPathParts({
    protocol: farm.protocol,
    quoteTokenAddress: farm.quoteToken.address,
    tokenAddress: farm.token.address,
  })
  const addLiquidityUrl = `/add/${liquidityUrlPathParts}`
  const { lpAddress } = farm

  return (
    <StyledCard>
      <FarmCardInnerContainer>
        <CardHeading
          lpLabel={lpLabel}
          multiplier={farm.multiplier}
          token={farm.token}
          quoteToken={farm.quoteToken}
          protocol={farm.protocol}
        />
        {!removed && (
          <Flex justifyContent="space-between" alignItems="center">
            <Text>{t('APR')}:</Text>
            <Text bold style={{ display: 'flex', alignItems: 'center' }}>
              {farm.apr ? (
                <ApyButton
                  variant="text-and-button"
                  pid={farm.pid}
                  lpTokenPrice={farm.lpTokenPrice}
                  lpSymbol={farm.lpSymbol}
                  multiplier={farm.multiplier}
                  lpLabel={lpLabel}
                  addLiquidityUrl={addLiquidityUrl}
                  incPrice={incPrice}
                  apr={farm.apr}
                  displayApr={displayApr}
                  lpRewardsApr={farm.lpRewardsApr}
                />
              ) : (
                <Skeleton height={24} width={80} />
              )}
            </Text>
          </Flex>
        )}
        <Flex justifyContent="space-between">
          <Text>{t('Earn')}:</Text>
          <Text bold>{earnLabel}</Text>
        </Flex>
        <CardActionsContainer
          farm={farm}
          lpLabel={lpLabel}
          account={account}
          addLiquidityUrl={addLiquidityUrl}
          displayApr={displayApr}
        />
      </FarmCardInnerContainer>

      <ExpandingWrapper>
        <ExpandableSectionButton
          onClick={() => setShowExpandableSection(!showExpandableSection)}
          expanded={showExpandableSection}
        />
        {showExpandableSection && (
          <DetailsSection
            removed={removed}
            scanAddressLink={getBlockExplorerLink(lpAddress, 'address', chainId)}
            infoAddress={farm.protocol === 'V1' ? `/info/pool/${lpAddress}` : `/info/v2/pool/${lpAddress}`}
            totalValueFormatted={totalValueFormatted}
            lpLabel={lpLabel}
            addLiquidityUrl={addLiquidityUrl}
            weight={farm.poolWeight ?? new BigNumber(0)}
          />
        )}
      </ExpandingWrapper>
    </StyledCard>
  )
}

export default FarmCard
